"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import parse from "html-react-parser";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { fetchFaqData, resolveFaqParams } from "@/app/api/service/faq";
import type { FaqItem, FaqQuestion, ProductFaqData } from "@/types/faq.model";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";
import { cn } from "@/lib/utils";

/** Visible Q&As before “Load More” — matches Figma web/mobile folds. */
const INITIAL_VISIBLE = 3;
const SECTION_FALLBACK = "General";

export type FaqProps = {
    /** API productType query param (e.g. broking, fd, bonds). */
    product?: string;
    /** API path segment (e.g. generic, referral). Defaults to generic. */
    subProduct?: string;
    /** Server-fetched / hardcoded FAQ payload — skips client fetch for select products. */
    initialFaqData?: ProductFaqData | null;
    /** Desktop “Find More Answers” link. Hidden when omitted. */
    moreAnswersHref?: string;
    moreAnswersEvent?: string;
    className?: string;
};

function usesInitialDataOnly(product: string, initialFaqData?: ProductFaqData | null) {
    return (
        product === "icu-transfer" ||
        product === "global" ||
        (product === "bullion" && !!initialFaqData)
    );
}

function sectionLabel(section: FaqQuestion, index: number) {
    const label = section.section?.trim();
    return label || (index === 0 ? SECTION_FALLBACK : `${SECTION_FALLBACK} ${index + 1}`);
}

function sectionKey(section: FaqQuestion, index: number) {
    return `${sectionLabel(section, index)}-${index}`;
}

/** Scroll a tab into the visible area of its horizontal tablist (no page scroll). */
function scrollTabIntoView(
    list: HTMLElement | null,
    tab: HTMLElement | null,
    pad = 16,
) {
    if (!list || !tab) return;

    const listRect = list.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    if (tabRect.left < listRect.left + pad) {
        list.scrollBy({
            left: tabRect.left - listRect.left - pad,
            behavior: "smooth",
        });
    } else if (tabRect.right > listRect.right - pad) {
        list.scrollBy({
            left: tabRect.right - listRect.right + pad,
            behavior: "smooth",
        });
    }
}

const tabListScrollClass =
    "w-full overflow-x-auto overscroll-x-contain [scrollbar-width:thin] [scrollbar-color:#D4D4D4_transparent] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#D4D4D4]";

/**
 * ICM 3.0 FAQ — Figma web 881:26730 / mobile 2081:21969
 * Same data architecture as `app/components/faq.tsx`; sections render as tabs.
 */
const Faq = ({
    product,
    subProduct,
    initialFaqData,
    moreAnswersHref,
    moreAnswersEvent = "FAQ_Find_More_Answers",
    className = "",
}: FaqProps) => {
    const { product: resolvedProduct, subProduct: resolvedSubProduct } = resolveFaqParams(
        product,
        subProduct,
    );
    const initialOnly = usesInitialDataOnly(resolvedProduct, initialFaqData);

    const { data: fetchedFaqData, isFetched } = useQuery({
        queryKey: ["faqData", resolvedProduct, resolvedSubProduct],
        queryFn: () => fetchFaqData(resolvedProduct, resolvedSubProduct),
        enabled: !initialOnly,
        initialData: initialOnly && initialFaqData ? initialFaqData : undefined,
    });

    const faqData = initialOnly ? initialFaqData : fetchedFaqData;
    const isReady = initialOnly ? !!initialFaqData : isFetched;

    const sections = useMemo<FaqQuestion[]>(() => {
        const list = faqData?.data?.questions;
        return Array.isArray(list) ? list.filter((s) => s?.content?.length) : [];
    }, [faqData]);

    const [activeTab, setActiveTab] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [openItem, setOpenItem] = useState<string | undefined>(undefined);
    const desktopTabListRef = useRef<HTMLDivElement>(null);
    const mobileTabListRef = useRef<HTMLDivElement>(null);
    const activeDesktopTabRef = useRef<HTMLButtonElement>(null);
    const activeMobileTabRef = useRef<HTMLButtonElement>(null);

    // Keep tab index in range when data changes
    useEffect(() => {
        if (activeTab >= sections.length) {
            setActiveTab(0);
        }
    }, [sections.length, activeTab]);

    // Reset accordion / load-more when switching tabs
    useEffect(() => {
        setShowAll(false);
        setOpenItem(undefined);
    }, [activeTab]);

    // Keep active tab visible when selection changes (desktop + mobile)
    useEffect(() => {
        scrollTabIntoView(desktopTabListRef.current, activeDesktopTabRef.current);
        scrollTabIntoView(mobileTabListRef.current, activeMobileTabRef.current);
    }, [activeTab]);

    if (!isReady || sections.length === 0) {
        return null;
    }

    const safeTab = Math.min(activeTab, sections.length - 1);
    const activeSection = sections[safeTab];
    const questions = activeSection?.content ?? [];
    const visibleQuestions = showAll ? questions : questions.slice(0, INITIAL_VISIBLE);
    const canLoadMore = questions.length > INITIAL_VISIBLE;
    const showTabs = sections.length > 1;

    const selectTab = (index: number, tabEl: HTMLButtonElement | null) => {
        setActiveTab(index);
        // Immediate scroll on click (before paint of active ref swap)
        const list =
            tabEl?.closest<HTMLElement>('[role="tablist"]') ??
            (window.matchMedia("(min-width: 768px)").matches
                ? desktopTabListRef.current
                : mobileTabListRef.current);
        scrollTabIntoView(list, tabEl);
    };

    const renderTabButton = (
        section: FaqQuestion,
        index: number,
        variant: "desktop" | "mobile",
    ) => {
        const isActive = index === safeTab;
        const label = sectionLabel(section, index);
        const id = `faq-tab-${index}`;

        if (variant === "desktop") {
            return (
                <button
                    key={sectionKey(section, index)}
                    ref={isActive ? activeDesktopTabRef : undefined}
                    type="button"
                    role="tab"
                    id={id}
                    aria-selected={isActive}
                    aria-controls="faq-tabpanel"
                    onClick={(e) => selectTab(index, e.currentTarget)}
                    className={cn(
                        "relative shrink-0 whitespace-nowrap px-6 py-2 text-lg font-medium leading-6 transition-colors",
                        isActive ? "text-brand-primary-500" : "text-black",
                    )}
                >
                    {label}
                    <span
                        aria-hidden
                        className={cn(
                            "absolute inset-x-0 bottom-0 h-0.5",
                            isActive ? "bg-brand-primary-500" : "bg-transparent",
                        )}
                    />
                </button>
            );
        }

        return (
            <button
                key={sectionKey(section, index)}
                ref={isActive ? activeMobileTabRef : undefined}
                type="button"
                role="tab"
                id={id}
                aria-selected={isActive}
                aria-controls="faq-tabpanel"
                onClick={(e) => selectTab(index, e.currentTarget)}
                className={cn(
                    "h-7 shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium leading-5 transition-colors",
                    isActive
                        ? "bg-white text-brand-primary-500 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
                        : "bg-transparent text-[#767676]",
                )}
            >
                {label}
            </button>
        );
    };

    return (
        <section
            id="section-faq"
            aria-labelledby="faq-heading"
            className={cn(
                "mt-[60px] md:mt-[120px] w-full bg-white px-4 md:px-[120px]",
                className,
            )}
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 md:gap-12">
                <header className="flex w-full flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-0">
                    <div className="flex w-full flex-col gap-1 md:max-w-[728px] md:gap-2">
                        <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px]">
                            FAQs
                        </p>
                        <h2
                            id="faq-heading"
                            className="w-full text-2xl font-bold leading-8 text-black md:text-5xl md:leading-[58px]"
                        >
                            We&apos;re here to help!
                        </h2>
                    </div>

                    {moreAnswersHref ? (
                        <TrackedLink
                            href={moreAnswersHref}
                            event={moreAnswersEvent}
                            className="hidden h-10 shrink-0 items-center justify-center gap-2 rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-6 py-2.5 text-base font-medium leading-[22px] text-cta-secondary-text no-underline transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover md:inline-flex"
                        >
                            Find More Answers
                            <GrowWealthChevron />
                        </TrackedLink>
                    ) : null}
                </header>

                <div className="flex w-full flex-col gap-6 md:gap-12">
                    {showTabs ? (
                        <>
                            {/* Desktop underline tabs */}
                            <div
                                ref={desktopTabListRef}
                                role="tablist"
                                aria-label="FAQ categories"
                                className={cn(
                                    "hidden border-b border-surface-stroke md:flex pb-2",
                                    tabListScrollClass,
                                )}
                            >
                                {sections.map((section, index) =>
                                    renderTabButton(section, index, "desktop"),
                                )}
                            </div>

                            {/* Mobile pill tabs */}
                            <div
                                ref={mobileTabListRef}
                                role="tablist"
                                aria-label="FAQ categories"
                                className={cn(
                                    "-mx-4 flex gap-1 border-b border-surface-stroke px-4 pb-0 md:hidden",
                                    tabListScrollClass,
                                )}
                            >
                                {sections.map((section, index) =>
                                    renderTabButton(section, index, "mobile"),
                                )}
                            </div>
                        </>
                    ) : null}

                    <div
                        id="faq-tabpanel"
                        role="tabpanel"
                        aria-labelledby={`faq-tab-${safeTab}`}
                        className="flex w-full flex-col gap-6"
                    >
                        <Accordion
                            type="single"
                            collapsible
                            value={openItem}
                            onValueChange={setOpenItem}
                            className="flex w-full flex-col gap-6"
                        >
                            {visibleQuestions.map((item: FaqItem, index: number) => (
                                <AccordionItem
                                    key={`${safeTab}-${item.ques}-${index}`}
                                    value={`q-${index}`}
                                    className="overflow-hidden rounded-2xl border border-solid !border-surface-stroke bg-white data-[state=closed]:!border-solid data-[state=open]:!border-solid data-[state=open]:!border-secondary-500"
                                >
                                    <AccordionTrigger
                                        className="gap-3 px-4 py-4 text-left text-base font-semibold leading-[22px] text-[#0F1016] hover:no-underline md:py-6 md:text-lg md:leading-6 [&>svg]:size-6 [&>svg]:shrink-0"
                                    >
                                        <span className="pr-1">{item.ques}</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-4 pt-0 text-left text-base font-regular leading-[22px] text-[#767676] md:pb-6">
                                        {parse(item.ans)}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>

                        {canLoadMore ? (
                            <button
                                type="button"
                                onClick={() => setShowAll((prev) => !prev)}
                                className="mx-auto inline-flex h-[26px] items-center justify-center gap-1.5 text-sm font-medium leading-5 text-secondary-500"
                            >
                                <span className="underline decoration-solid underline-offset-2">
                                    {showAll ? "Show Less" : "Load More"}
                                </span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden
                                    className={cn(
                                        "size-3 shrink-0 transition-transform md:size-4",
                                        showAll && "rotate-180",
                                    )}
                                >
                                    <path
                                        d="M4 6L8 10L12 6"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Faq;
